/* eslint-disable react/jsx-no-constructed-context-values */
import useStorageState from 'hooks/useStorageState'
import { useTokenApi } from 'hooks/useTokenApi'
import { createContext, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { toast } from 'react-toastify'
import api from 'services/api'
import { isCurrentPathAnonymousBrandFlow } from '@cannect/config/brands.config'
import { isWhatsAppCheckoutPath } from 'utils/renderActions'
import { fromResponseUserToUser, INITIAL_USER } from './helpers'
import { FetchUserResponse, SignedUserProviderProps, User } from './types'

const SignedUserContext = createContext({} as SignedUserContextValue)

interface SignedUserContextValue {
  signedUser: User
  isUserLoading: boolean
  refetchSignedUser: () => void
  shouldOpenPrescriberTerms: boolean
  setShouldOpenPrescriberTerms: (value: boolean) => void
}

export function SignedUserProvider({ children }: SignedUserProviderProps) {
  const history = useHistory()
  const [signedUser, setSignedUser] = useStorageState<User>('@CANNECT_SIGNED_USER', INITIAL_USER)
  const [isUserLoading, setIsUserLoading] = useState(false)
  const [shouldOpenPrescriberTerms, setShouldOpenPrescriberTerms] = useState(false)
  const { tokenApi, deleteTokenApi } = useTokenApi()

  useEffect(() => {
    if (!tokenApi) {
      setSignedUser(INITIAL_USER)
    }
  }, [tokenApi])

  const refetchSignedUser = async () => {
    setIsUserLoading(true)
    try {
      const response = await api.get<FetchUserResponse>(`/my-account/information`)
      setSignedUser(fromResponseUserToUser(response.data))
      if (
        response.data.prescriber &&
        response.data.user.accepted_terms_of_use === false &&
        response.data.prescriber.listed
      ) {
        setShouldOpenPrescriberTerms(true)
      }
    } catch (err: any) {
      console.log(err)
      if (err.response?.status === 401) {
        deleteTokenApi()
        setShouldOpenPrescriberTerms(false)
        const lowerPath = window.location.pathname.toLowerCase()
        const isAspaecFlow = lowerPath.startsWith('/aspaec') || lowerPath.startsWith('/areaassociados')
        // Whitelabel sem login: não há para onde redirecionar. Limpa o token e
        // segue anônimo — o cadastro simplificado reautentica adiante no funil.
        if (!isWhatsAppCheckoutPath(lowerPath) && !isCurrentPathAnonymousBrandFlow()) {
          history.push(isAspaecFlow ? '/AreaAssociados' : '/login')
        }
        return
      }
      toast.error('Não foi possível atualizar os dados do usuário logado. Tente deslogar e logar novamente.')
    }
    setIsUserLoading(false)
  }

  useEffect(() => {
    if (!tokenApi) return
    // Marcas whitelabel sem login (Remederi/Vikura) não podem chamar rota logada:
    // o token aqui costuma ser resíduo de uma sessão Cannect anterior no mesmo
    // browser, e o 401 resultante expulsava o paciente do funil da marca.
    if (isCurrentPathAnonymousBrandFlow()) return
    refetchSignedUser()

    const raw = sessionStorage.getItem('@CANNECT:PENDING_NOTIFY_ME')
    if (!raw) return
    try {
      const { productId, source } = JSON.parse(raw)
      sessionStorage.removeItem('@CANNECT:PENDING_NOTIFY_ME')
      if (productId && source) {
        api.post(`/product/${productId}/notify-me`, { source }).catch(() => {})
      }
    } catch {
      sessionStorage.removeItem('@CANNECT:PENDING_NOTIFY_ME')
    }
  }, [tokenApi])

  return (
    <SignedUserContext.Provider
      value={{
        signedUser,
        refetchSignedUser,
        isUserLoading,
        shouldOpenPrescriberTerms,
        setShouldOpenPrescriberTerms
      }}>
      {children}
    </SignedUserContext.Provider>
  )
}

export default function useSignedUser() {
  return useContext(SignedUserContext)
}
